import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #pendingNavigationTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onMenuLinkClick, true);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onMenuLinkClick, true);
    this.#clearPendingNavigationTimeout();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Adds immediate visual feedback when a drawer link is clicked.
   * @param {Event} event
   */
  #onMenuLinkClick = (event) => {
    const targetElement = this.#getEventElement(event);
    if (!(targetElement instanceof Element)) return;

    const clickedLink = targetElement.closest('a.menu-drawer__menu-item[href]');
    if (!(clickedLink instanceof HTMLAnchorElement)) return;
    if (!this.contains(clickedLink)) return;

    if (!this.#shouldInterceptNavigation(event, clickedLink)) {
      this.#setPendingLink(clickedLink);
      return;
    }

    this.#navigateWithFeedback(clickedLink.href, event, clickedLink);
  };

  /**
   * @param {Event | undefined} event
   * @returns {Element | null}
   */
  #getEventElement(event) {
    const path = typeof event?.composedPath === 'function' ? event.composedPath() : [];
    const pathElement = path.find((node) => node instanceof Element);

    if (pathElement instanceof Element) return pathElement;
    if (event?.target instanceof Element) return event.target;
    if (event?.target instanceof Node) return event.target.parentElement;
    return null;
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Submenus are bound to <summary> clicks. On some touch devices (notably iOS),
    // rapid taps can trigger native open/close toggles and feel inconsistent.
    // We take control of submenu toggling here to keep it deterministic:
    // one tap opens, and accidental second taps do not immediately close it.
    if (target) {
      const submenuUrl = summary.dataset.submenuUrl;
      const isSubmenuToggleAction = target === 'submenu-toggle';
      const isKeyboardActivation = event instanceof MouseEvent && event.detail === 0;

      // For menu items that have both a category page and children:
      // - tap/click on text => navigate to category page
      // - tap/click on chevron => open submenu
      if (!isSubmenuToggleAction && submenuUrl && submenuUrl !== '#' && !isKeyboardActivation) {
        window.location.assign(submenuUrl);
        return;
      }

      event?.preventDefault?.();

      if (details.hasAttribute('open')) return;
      details.setAttribute('open', '');
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Force navigation for direct menu links in drawer on touch devices.
   * @param {{url?: string}} data
   * @param {Event} [event]
   */
  navigate(data, event) {
    const url = data?.url;
    if (!url || url === '#') return;

    const targetElement = this.#getEventElement(event);
    const clickedLink = targetElement?.closest?.('a.menu-drawer__menu-item[href]');

    if (clickedLink instanceof HTMLAnchorElement && !this.#shouldInterceptNavigation(event, clickedLink)) {
      this.#setPendingLink(clickedLink);
      return;
    }

    this.#navigateWithFeedback(url, event, clickedLink instanceof HTMLAnchorElement ? clickedLink : null);
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * @param {HTMLAnchorElement} link
   */
  #setPendingLink(link) {
    this.#clearPendingLinks();
    link.classList.add('menu-drawer__menu-item--pending');
  }

  /**
   * @param {Event | undefined} event
   * @param {HTMLAnchorElement} link
   * @returns {boolean}
   */
  #shouldInterceptNavigation(event, link) {
    const href = link.getAttribute('href');
    if (!href || href === '#' || href.startsWith('#')) return false;
    if (link.hasAttribute('download')) return false;
    if (link.target && link.target !== '_self') return false;

    if (event instanceof MouseEvent) {
      if (event.button !== 0) return false;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    }

    return true;
  }

  /**
   * @param {string} url
   * @param {Event | undefined} event
   * @param {HTMLAnchorElement | null} link
   */
  #navigateWithFeedback(url, event, link) {
    if (!url) return;

    if (link instanceof HTMLAnchorElement) {
      this.#setPendingLink(link);
    }

    event?.preventDefault?.();
    event?.stopPropagation?.();

    this.#clearPendingNavigationTimeout();
    this.#pendingNavigationTimeout = window.setTimeout(() => {
      window.location.assign(url);
    }, 90);
  }

  #clearPendingNavigationTimeout() {
    if (this.#pendingNavigationTimeout === null) return;
    window.clearTimeout(this.#pendingNavigationTimeout);
    this.#pendingNavigationTimeout = null;
  }

  #clearPendingLinks() {
    const pendingLinks = this.querySelectorAll('.menu-drawer__menu-item--pending');
    pendingLinks.forEach((item) => item.classList.remove('menu-drawer__menu-item--pending'));
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          this.#clearPendingNavigationTimeout();
          this.#clearPendingLinks();
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
